import { useState, type JSX } from 'react';

import { shaktiApi, ApiError } from '../api/client';
import { useProjectScopedEffect } from '../stores/project';

/**
 * T1 (v2.0) — Rate-limits admin surface.
 *
 * Renders every active GCRA cell the server is tracking plus its
 * current load ratio, sorted with hottest keys first. Auto-refreshes
 * every 10 s so operators watching a spike see it update in real time.
 * Admin-gated server-side (`/v1/admin/rate-limits/usage` requires an
 * Admin capability); unauthorised tenants get a 403 which we surface
 * as an inline notice.
 */
interface Cell {
  dimension: string;
  entity_id: string;
  limit: number;
  period_secs: number;
  burst: number;
  current_load_ratio: number;
  last_accessed: string;
}

interface Snapshot {
  cells: Cell[];
  total_active_cells: number;
  rate_limit_hits_total: number;
}

const REFRESH_MS = 10_000;

export function RateLimitsRoute(): JSX.Element {
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [tick, setTick] = useState(0);

  useProjectScopedEffect(
    (signal) => {
      setError(null);
      shaktiApi
        .getRateLimitsUsage({ signal })
        .then((s) => {
          if (signal.aborted) return;
          setSnap(s);
        })
        .catch((e: unknown) => {
          if (signal.aborted) return;
          setError(
            e instanceof ApiError
              ? `${e.body?.error ?? e.message} (${e.status})`
              : e instanceof Error
                ? e.message
                : 'Failed to load rate-limit usage',
          );
        });
      // Auto-refresh by bumping a dep on a timer.
      const id = setInterval(() => {
        if (!signal.aborted) setTick((t) => t + 1);
      }, REFRESH_MS);
      return () => clearInterval(id);
    },
    [tick],
  );

  const cells = (snap?.cells ?? []).filter((c) =>
    filter.trim()
      ? `${c.dimension}:${c.entity_id}`
          .toLowerCase()
          .includes(filter.trim().toLowerCase())
      : true,
  );

  return (
    <section
      aria-labelledby="rl-title"
      className="mx-auto flex max-w-6xl flex-col gap-4"
    >
      <header>
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          /dashboard/rate-limits
        </span>
        <h1 id="rl-title" className="text-2xl font-semibold text-fg-primary">
          Rate limits
        </h1>
        <p className="text-sm text-fg-secondary">
          Live per-key GCRA load, auto-refreshed every {REFRESH_MS / 1000}s.
          Admin-only — non-admin operators see a 403 here.
        </p>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <Stat
          label="Active cells"
          value={snap ? snap.total_active_cells.toLocaleString() : '—'}
        />
        <Stat
          label="Rate-limit hits (lifetime)"
          value={snap ? snap.rate_limit_hits_total.toLocaleString() : '—'}
        />
        <Stat
          label="Hot keys (≥ 80% load)"
          value={
            snap
              ? cells.filter((c) => c.current_load_ratio >= 0.8).length.toString()
              : '—'
          }
        />
      </div>

      {error ? (
        <div
          role="alert"
          className="shakti-card border border-status-error/40 text-status-error"
        >
          {error}
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by dimension or entity…"
          aria-label="Filter rate-limit cells"
          className="w-72 rounded-sm border border-border-subtle bg-bg-surface px-3 py-1.5 text-sm"
        />
        <span className="text-2xs text-fg-muted">
          Showing {cells.length} of {snap?.total_active_cells ?? 0}
        </span>
      </div>

      <div
        className="shakti-card overflow-x-auto p-0"
        aria-label="Rate-limit cells"
      >
        <table className="w-full text-left text-xs">
          <thead className="bg-bg-raised text-2xs uppercase tracking-wider text-fg-muted">
            <tr>
              <th className="px-3 py-2">Dimension</th>
              <th className="px-3 py-2">Entity</th>
              <th className="px-3 py-2">Limit</th>
              <th className="px-3 py-2">Burst</th>
              <th className="px-3 py-2">Load</th>
              <th className="px-3 py-2">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {cells.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-fg-muted">
                  {snap ? 'No active rate-limit cells.' : 'Loading…'}
                </td>
              </tr>
            ) : (
              cells.map((c) => (
                <tr
                  key={`${c.dimension}:${c.entity_id}`}
                  className="border-t border-border-subtle"
                >
                  <td className="px-3 py-2 shakti-mono">{c.dimension}</td>
                  <td className="px-3 py-2 shakti-mono">{c.entity_id}</td>
                  <td className="px-3 py-2">
                    {c.limit} / {c.period_secs}s
                  </td>
                  <td className="px-3 py-2">{c.burst}</td>
                  <td className="px-3 py-2">
                    <LoadBar ratio={c.current_load_ratio} />
                  </td>
                  <td className="px-3 py-2 text-fg-muted">
                    {new Date(c.last_accessed).toLocaleTimeString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="shakti-card flex flex-col gap-0.5">
      <span className="text-2xs uppercase tracking-wider text-fg-muted">
        {label}
      </span>
      <span className="text-lg font-semibold text-fg-primary">{value}</span>
    </div>
  );
}

function LoadBar({ ratio }: { ratio: number }): JSX.Element {
  const pct = Math.max(0, Math.min(100, ratio * 100));
  const tone =
    pct >= 85
      ? 'bg-status-error'
      : pct >= 65
        ? 'bg-status-warning'
        : 'bg-status-success';
  return (
    <div className="flex items-center gap-2">
      <div
        className="h-1.5 w-24 rounded bg-bg-raised"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
      >
        <div
          className={`h-full rounded ${tone}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="shakti-mono text-2xs text-fg-muted">
        {Math.round(pct)}%
      </span>
    </div>
  );
}
